import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { jwtDecode } from "jwt-decode";
import API from "../services/api";
// Keycloak disabled for now.
// import {
//   buildLogoutUrl,
//   clearSession,
//   completeKeycloakLogin,
//   consumePostLoginRedirect,
//   ensureFreshAccessToken,
//   getCurrentSession,
//   isTokenExpired,
//   loadLoginTransaction,
//   rememberPostLoginRedirect,
//   startKeycloakLogin,
// } from "../services/keycloakSession";

const AuthContext = createContext(null);
const SESSION_KEY = "admin.session";
const LEGACY_TOKEN_KEY = "token";
const REDIRECT_KEY = "admin.login.redirect_to";

const parseToken = (token) => {
  try {
    return token ? jwtDecode(token) : null;
  } catch (error) {
    return null;
  }
};

const buildSessionFromToken = (token) => {
  const payload = parseToken(token);

  if (!token || !payload) {
    return null;
  }

  return {
    token,
    user: {
      id: payload.id,
      email: payload.email || "",
      role: payload.role || "Guest",
      roles: payload.role ? [payload.role] : [],
    },
    exp: payload.exp,
  };
};

const getCurrentSession = () => {
  const rawSession = localStorage.getItem(SESSION_KEY);

  if (rawSession) {
    try {
      const parsed = JSON.parse(rawSession);
      if (parsed?.token) {
        return parsed;
      }
    } catch (error) {
      localStorage.removeItem(SESSION_KEY);
    }
  }

  const legacyToken = localStorage.getItem(LEGACY_TOKEN_KEY);
  return buildSessionFromToken(legacyToken);
};

const isSessionExpired = (session) => {
  if (!session?.exp) {
    return false;
  }

  return session.exp * 1000 <= Date.now();
};

const persistSession = (token) => {
  const session = buildSessionFromToken(token);

  if (!session) {
    return null;
  }

  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  localStorage.setItem(LEGACY_TOKEN_KEY, token);
  return session;
};

const clearSession = () => {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(LEGACY_TOKEN_KEY);
};

const rememberPostLoginRedirect = (redirectTo) => {
  if (redirectTo) {
    sessionStorage.setItem(REDIRECT_KEY, redirectTo);
  }
};

const consumePostLoginRedirect = () => {
  const redirectTo = sessionStorage.getItem(REDIRECT_KEY);
  sessionStorage.removeItem(REDIRECT_KEY);
  return redirectTo;
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(() => getCurrentSession());
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!getCurrentSession()?.token);
  const [isInitialized, setIsInitialized] = useState(false);

  const syncSession = useCallback(() => {
    const currentSession = getCurrentSession();
    setSession(currentSession);
    setIsAuthenticated(!!currentSession?.token);
    return currentSession;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = () => {
      const currentSession = getCurrentSession();

      if (isSessionExpired(currentSession)) {
        clearSession();
      }

      if (cancelled) {
        return;
      }

      syncSession();
      setIsInitialized(true);
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [syncSession]);

  // Keycloak disabled for now.
  // useEffect(() => {
  //   if (!session?.accessToken || !session?.refreshToken) {
  //     return undefined;
  //   }
  //
  //   const interval = window.setInterval(async () => {
  //     const refreshedAccessToken = await ensureFreshAccessToken(120);
  //
  //     if (refreshedAccessToken) {
  //       syncSession();
  //     }
  //   }, 60000);
  //
  //   return () => window.clearInterval(interval);
  // }, [session?.accessToken, session?.refreshToken, syncSession]);

  const login = useCallback(
    async ({ email, password, redirectTo } = {}) => {
      const targetRedirect = redirectTo || "/dashboard";
      rememberPostLoginRedirect(targetRedirect);

      try {
        const response = await API.post("/login", {
          email,
          password,
        });
        const token = response.data?.data?.token || response.data?.token;
        const nextSession = persistSession(token);

        if (!nextSession) {
          throw new Error("Login response did not include a valid token.");
        }

        setSession(nextSession);
        setIsAuthenticated(true);
        return nextSession;
      } catch (error) {
        throw error;
      }
    },
    []
  );

  // Keycloak disabled for now.
  // const completeLogin = useCallback(async ({ code, state }) => {
  //   const completedSession = await completeKeycloakLogin({ code, state });
  //   setSession(completedSession);
  //   setIsAuthenticated(true);
  //   return completedSession;
  // }, []);
  const completeLogin = useCallback(async () => null, []);

  const logout = useCallback(async () => {
    // Keycloak disabled for now.
    // const currentSession = getCurrentSession();
    clearSession();
    setSession(null);
    setIsAuthenticated(false);

    // const logoutUrl = buildLogoutUrl(currentSession?.idToken);
    window.location.assign("/signin");
  }, []);

  const refreshSession = useCallback(async () => {
    const currentSession = syncSession();

    if (!currentSession || isSessionExpired(currentSession)) {
      clearSession();
      setSession(null);
      setIsAuthenticated(false);
      return null;
    }

    return currentSession;
  }, [syncSession]);

  const getAccessToken = useCallback(() => getCurrentSession()?.token || null, []);

  const value = useMemo(
    () => ({
      session,
      user: session?.user || null,
      isAuthenticated,
      isInitialized,
      login,
      completeLogin,
      logout,
      refreshSession,
      getAccessToken,
      syncSession,
      consumePostLoginRedirect,
      rememberPostLoginRedirect,
      clearSession,
    }),
    [
      session,
      isAuthenticated,
      isInitialized,
      login,
      completeLogin,
      logout,
      refreshSession,
      getAccessToken,
      syncSession,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
